/**
 * Scroll reveal.
 *
 * Fades content up as it enters the viewport. Paired with the `[data-reveal]` rules in
 * assets/premium.css.
 *
 * WHY THIS IS WRITTEN THE WAY IT IS
 * --------------------------------
 * The obvious implementation — hide everything in CSS, reveal with JS — is a liability on a
 * production storefront: if the script fails to load, throws, or is blocked, the page stays
 * invisible. Every decision below exists to make that impossible.
 *
 *   1. FAILS OPEN. The hidden state lives behind `[data-reveal]`, and only this script ever
 *      applies it. No script, no attribute, no hidden content.
 *
 *   2. NEVER TOUCHES ABOVE THE FOLD. Only elements that begin fully below the viewport are
 *      considered. There is therefore no flash (nothing visible is hidden after paint), and
 *      the LCP element — the hero — is never delayed, transformed or faded.
 *
 *   3. EVERY HIDDEN ELEMENT IS REACHABLE. This is the subtle one. A negative bottom
 *      `rootMargin` shrinks the observer's root, which creates a dead zone at the very end of
 *      the document: an element shorter than that margin, sitting last on the page, can never
 *      intersect the shrunken root even at maximum scroll — so it would keep `[data-reveal]`
 *      and stay at `opacity: 0` forever. The footer utilities bar is exactly that element.
 *      Hence `rootMargin: 0` and `threshold: 0`: anything that can be scrolled into view at
 *      all is guaranteed to intersect. For the same reason `threshold` is NOT raised — a
 *      section taller than 1/threshold viewports could never reach the required ratio.
 *
 *   4. CLEANS UP AFTER ITSELF. The attribute is removed once the transition finishes. This
 *      matters beyond hygiene: any `translate` other than `none` makes an element the
 *      containing block for its `position: fixed` descendants. Removing the attribute drops
 *      the transform — and the compositor layer with it — rather than leaving `translate: 0 0`
 *      behind, which would still create one.
 *
 * No dependencies, no animation library. One IntersectionObserver, compositor-only properties
 * (opacity + translate), and the observer detaches from each element as it fires.
 */

const REVEAL_ATTR = 'data-reveal';
const STAGGER_ATTR = 'data-reveal-stagger';

/** Longest transition (280ms) + longest stagger (200ms), plus headroom. */
const CLEANUP_TIMEOUT = 700;

/**
 * Grids whose children arrive as a staggered sequence rather than one block.
 *
 * ONLY `.product-grid` — a real grid of `<li class="product-grid__item">`. Deliberately not
 * `.resource-list` (featured products, collection lists): that renders as a horizontal
 * carousel under some settings, and staggering slides inside a horizontal scroller reveals
 * them one-by-one as the shopper swipes, which is not the effect. Those sections reveal as a
 * single block instead, which is both safer and closer to the intent.
 *
 * Note `.section-resource-list__content` is NOT this container — it wraps the section's editor
 * blocks (heading, "View all" button), not the cards.
 */
const GRID_SELECTOR = '.product-grid';

/**
 * Sections containing these are never revealed.
 *
 * A `translate` other than `none` turns an element into the containing block for its
 * `position: fixed` descendants, so a fixed bar inside a translated section would anchor to
 * the section instead of the viewport and drift with the page.
 *
 * Most of the theme's fixed UI is immune because it lives in the top layer (`<dialog>` opened
 * with `showModal()`, native `[popover]`) or is rendered outside any section in
 * layout/theme.liquid — the cart drawer, menu drawer, modals and anchored popovers all
 * qualify. These two do not:
 *
 *   - `<sticky-add-to-cart>` is a plain fixed element inside the product section.
 *   - `<theme-drawer>` falls back to `.show()` rather than `.showModal()` on desktop, so it
 *     is NOT in the top layer; instances live inside the filters and buy-buttons blocks.
 */
const FIXED_CONTENT_SELECTOR = 'sticky-add-to-cart, theme-drawer';

/** @type {IntersectionObserver | undefined} */
let observer;

/**
 * Read live rather than cached, so a mid-session OS change is honoured.
 * @returns {boolean}
 */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Drops the reveal attribute once the element has finished arriving.
 * @param {Element} element
 */
function scheduleCleanup(element) {
  let done = false;

  // Typed as `Event`, not `TransitionEvent`: under `strictFunctionTypes` a handler taking the
  // narrower type is not assignable to `EventListener`, which takes `Event`. `.target` is all
  // we need, and it lives on the base type.
  /** @param {Event} [event] */
  const finish = (event) => {
    // `transitionend` BUBBLES. A revealing section is full of descendants with their own
    // transitions — a product card's hover transform, a button's background. If the shopper's
    // cursor happens to rest on a card as it scrolls in, that card's transition would end,
    // bubble up, and tear the reveal attribute off mid-fade, snapping the element visible.
    // Only the element's own transition may complete the reveal.
    if (event && event.target !== element) return;
    if (done) return;

    done = true;
    element.removeEventListener('transitionend', finish);
    element.removeAttribute(REVEAL_ATTR);
  };

  element.addEventListener('transitionend', finish);

  // Safety net: `transitionend` never fires if the element is hidden, detached, or the
  // transition is interrupted. Without this the transform would persist indefinitely.
  setTimeout(finish, CLEANUP_TIMEOUT);
}

/**
 * @param {IntersectionObserverEntry[]} entries
 */
function onIntersect(entries) {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;

    const element = entry.target;
    observer?.unobserve(element);
    element.setAttribute(REVEAL_ATTR, 'in');
    scheduleCleanup(element);
  }
}

/**
 * Collects reveal targets under the given roots, then hides and observes them.
 *
 * Split into a read pass and a write pass on purpose. Interleaving `getBoundingClientRect()`
 * with `setAttribute()` would force a synchronous style-and-layout recalculation on every
 * iteration — each attribute write matches `[data-reveal]` and dirties style, and the next
 * read then has to flush it. On a collection page with fifty cards that is fifty forced
 * layouts, and it runs before first paint (this module is deferred, so it executes during
 * `interactive`), landing squarely on the critical path for FCP and LCP.
 *
 * @param {Element[]} sections
 */
function reveal(sections) {
  /** @type {Element[]} */
  const candidates = [];

  // --- Read pass: gather candidates, no writes. ---
  for (const section of sections) {
    if (section.querySelector(FIXED_CONTENT_SELECTOR)) continue;

    const grid = section.querySelector(GRID_SELECTOR);

    if (grid instanceof HTMLElement && grid.children.length > 0) {
      candidates.push(grid, ...grid.children);
      continue;
    }

    candidates.push(section);
  }

  const viewportHeight = window.innerHeight;

  /** @type {Element[]} */
  const pending = [];
  /** @type {Element[]} */
  const grids = [];

  for (const element of candidates) {
    if (element.hasAttribute(REVEAL_ATTR) || element.hasAttribute(STAGGER_ATTR)) continue;

    // A grid is carried through only to receive the stagger attribute; it is not revealed
    // itself (its children are), so it needs no measurement.
    if (element.matches(GRID_SELECTOR)) {
      grids.push(element);
      continue;
    }

    const { top, height } = element.getBoundingClientRect();

    // Skip anything already on screen — that is what keeps this off the LCP path and prevents
    // a flash — and anything with no box to speak of (an empty or display:none section, or the
    // inline <script> Horizon renders as the first child of a product grid). Hiding a
    // zero-height element achieves nothing but risk.
    if (height === 0 || top < viewportHeight) continue;

    pending.push(element);
  }

  // --- Write pass: all mutations together, after every measurement is done. ---
  for (const grid of grids) grid.setAttribute(STAGGER_ATTR, '');

  for (const element of pending) {
    element.setAttribute(REVEAL_ATTR, '');
    observer?.observe(element);
  }
}

/**
 * @param {ParentNode} root
 * @returns {Element[]}
 */
function sectionsWithin(root) {
  const found = [...root.querySelectorAll('.shopify-section')];

  // `shopify:section:load` hands us the section element itself, which `querySelectorAll` would
  // not match. Including it lets the editor re-scan only what actually changed, instead of
  // re-hiding every sibling section on the page on each settings tweak.
  if (root instanceof Element && root.matches('.shopify-section')) found.unshift(root);

  return found;
}

function init() {
  // Bail before doing any work at all — no observer, no layout reads, no attributes.
  if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;

  observer ??= new IntersectionObserver(onIntersect, {
    // See point 3 in the file header: any negative margin or raised threshold creates a class
    // of element that can never intersect, and would therefore stay hidden forever.
    rootMargin: '0px',
    threshold: 0,
  });

  reveal(sectionsWithin(document));
}

// This module is deferred, so it runs at `interactive` — DOM is parsed, and the readyState
// check is belt-and-braces for a non-module execution path.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Theme editor: sections are re-rendered on every settings change, which replaces their DOM
// and loses the observers.
document.addEventListener('shopify:section:load', (event) => {
  if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;

  const { target } = event;
  if (target instanceof Element) reveal(sectionsWithin(target));
});
